import { lazy } from 'react';

/**
 * Recovering from a deploy that happened while a tab was open.
 *
 * Every route is a lazy chunk with a content hash in its name. A deploy
 * replaces the chunk files, so a tab opened before it still asks for the OLD
 * names the first time it visits a page. CloudFront answers a missing file with
 * index.html (200, text/html) — the browser tries to run HTML as JavaScript,
 * webpack reports "Loading chunk N failed", and with nothing to catch it React
 * unmounted the whole app: a blank white page (reported from Firefox on the
 * live demo, which is redeployed almost daily; it happens in every browser).
 *
 * The cure is to load the NEW build: reload the page once. The reload is
 * guarded in sessionStorage so a chunk that is genuinely broken in the new
 * build cannot loop — the second failure inside the window goes to the error
 * boundary, which shows a Reload button instead.
 */

const RELOAD_KEY = 'sdos:chunk-reload-at';
const RELOAD_WINDOW_MS = 30 * 1000;

const CHUNK_ERROR_PATTERNS = [
  /Loading (CSS )?chunk [\w-]+ failed/i,          // webpack
  /expected expression, got '<'/i,                // Firefox running index.html as JS
  /Unexpected token '<'/i,                        // Chrome/Safari, same cause
  /Failed to fetch dynamically imported module/i, // Chrome native import()
  /error loading dynamically imported module/i,   // Firefox native import()
  /Importing a module script failed/i,            // Safari native import()
];

export const isChunkLoadError = (err) => {
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  const message = String(err.message || err);
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
};

/**
 * Reload the page to pick up the current build — at most once per
 * RELOAD_WINDOW_MS. Returns true when a reload was started. Storage that
 * cannot be read or written means the guard cannot be trusted, so it refuses
 * rather than risk a reload loop.
 */
export const reloadOnceForNewVersion = ({ now = Date.now(), storage, location } = {}) => {
  try {
    const store = storage || window.sessionStorage;
    const last = Number(store.getItem(RELOAD_KEY) || 0);
    if (last && now - last < RELOAD_WINDOW_MS) return false;
    store.setItem(RELOAD_KEY, String(now));
  } catch {
    return false;
  }
  (location || window.location).reload();
  return true;
};

/**
 * `React.lazy` that reloads into the new build when its chunk is gone. While
 * the reload is in flight the promise never settles, so Suspense keeps its
 * fallback instead of flashing an error.
 */
export const lazyWithReload = (factory) =>
  lazy(() =>
    factory().catch((err) => {
      if (isChunkLoadError(err) && reloadOnceForNewVersion()) {
        return new Promise(() => {});
      }
      throw err;
    })
  );
